import { Dispatch } from 'redux';
import { axiosInstance, request, requestConfig } from './base';
import {
  AdicionarExtratoRequest,
  AdicionarBloqueioEmLoteRequest,
  BloqueioDocumentType,
  BloqueioRequest,
  BloqueioResponse,
  CadastroInvestidorRequest,
  CertidaoTitularidadeRequest,
  DownloadResponse,
  ConciliacaoCDFilter,
  ConciliacaoContaDepositoResponse,
  ConciliacaoStatus,
  ExtratoResponse,
  FundoInvestidorRequest,
  InvestidorFundoResponse,
  GETConciliacaoCDFilterParams,
  GETConciliacoesContasDepositoResponse,
  GETBloqueiosFilterParams,
  GETInvestidoresFilterParams,
  GETInvestidoresParams,
  GetInvestidorInstrumentosParams,
  GETInvestidorPosicoesRequest,
  InvestidorDocumentResponse,
  InvestidorInstrumentoFinanceiroResponse,
  InvestidorResponse,
  PaginatedResult,
  PosicaoResponse,
  ResponseBloqueioEmLote,
  TipoCertidao,
  CertidaoInadimplenciaParams,
} from 'common/genericTypes';
import { Moment } from 'moment';

type BloqueioApiResponse = Partial<BloqueioResponse> & {
  Liberado?: boolean;
  codigo?: string;
  instrumentoFinanceiro?: { codigo?: string };
};

const buildBloqueiosPaginatedPayload = (filter?: GETBloqueiosFilterParams) => {
  const payload: any = {
    ...(filter ?? {}),
    bloqueiosFilterType: (filter as any)?.bloqueiosFilterType ?? (filter as any)?.bloqueiosFilter,
  };
  delete payload.bloqueiosFilter;
  return payload;
};

const mapBloqueioResponse = (item: BloqueioApiResponse): BloqueioResponse => {
  const liberado = item.liberado ?? item.Liberado;
  const codigo = item.instrumentoFinanceiro?.codigo ?? item.codigo;

  return {
    ...(item as any),
    liberado,
    instrumentoFinanceiro: item.instrumentoFinanceiro
      ? ({ ...item.instrumentoFinanceiro, codigo } as any)
      : ({ codigo } as any),
  } as BloqueioResponse;
};

export const GETInvestidor = async (dispatch: Dispatch, investidorId: string, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.get<InvestidorResponse>(`${requestConfig.api}api/v1/investidores/${investidorId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTInvestidorPaginated = async (
  dispatch: Dispatch,
  filter?: GETInvestidoresFilterParams,
  stackRequest: boolean = false
): Promise<PaginatedResult<InvestidorResponse>> => {
  return await request(
    () =>
      axiosInstance.post<PaginatedResult<InvestidorResponse>>(
        `${requestConfig.api}api/v1/investidores/paginated`,
        filter
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTInvestidor = async (
  dispatch: Dispatch,
  cadastroInvestidor: CadastroInvestidorRequest,
  stackRequest: boolean = true
) => {
  return await request(
    () => axiosInstance.post<InvestidorResponse>(`${requestConfig.api}api/v1/investidores`, cadastroInvestidor),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTInvestidor = async (
  dispatch: Dispatch,
  investidorId: string,
  cadastroInvestidor: CadastroInvestidorRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.put<InvestidorResponse>(
        `${requestConfig.api}api/v1/investidores/${investidorId}`,
        cadastroInvestidor
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETDocumentDownloadUrl = async (
  dispatch: Dispatch,
  investidorId: string,
  documentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<string>(
        `${requestConfig.api}api/v1/investidores/${investidorId}/document/${documentId}/download`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTUploadDoc = async (
  dispatch: Dispatch,
  investidorId: string,
  investidorDocumentType: string,
  file: File,
  stackRequest: boolean = false
) => {
  const data = new FormData();
  data.append('file', file);
  return await request(
    () =>
      axiosInstance.put<InvestidorDocumentResponse>(
        `${requestConfig.api}api/v1/investidores/${investidorId}/document`,
        data,
        {
          params: {
            investidorDocumentType,
          },
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETInvestidores = async (
  dispatch: Dispatch,
  params?: GETInvestidoresParams,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<Array<InvestidorResponse>>(
        `${requestConfig.api}api/v1/investidores`,
        params ? { params } : undefined
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETInvestidorByCpfCnpj = async (
  dispatch: Dispatch,
  cpfCnpj: string,
  participanteId: string,
  stackRequest: boolean = false
) => {
  const onlyDigits = cpfCnpj.replace(/\D/g, '');

  return await request(
    () =>
      axiosInstance.get<InvestidorResponse>(
        `${requestConfig.api}api/v1/investidores/cpf-cnpj/${onlyDigits}/${participanteId}`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const DELETEInvestidor = async (dispatch: Dispatch, investidorId?: string, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.delete<Array<InvestidorResponse>>(`${requestConfig.api}api/v1/investidores/${investidorId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTSuspenderInvestidor = async (
  dispatch: Dispatch,
  investidorId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.post<InvestidorResponse>(`${requestConfig.api}api/v1/investidores/${investidorId}/suspender`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTLiberarSuspensaoInvestidor = async (
  dispatch: Dispatch,
  investidorId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.post<InvestidorResponse>(
        `${requestConfig.api}api/v1/investidores/${investidorId}/liberar-suspensao`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETConciliacaoContaDepositoDownload = async (
  dispatch: Dispatch,
  conciliacaoContaDepositoId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<any>(
        `${requestConfig.api}api/v1/investidores/conciliacao/${conciliacaoContaDepositoId}/download`,
        {
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'gzip, deflate, br',
            Accept: '*/*',
          },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETConciliacaoContaDepositoDownloadPdf = async (
  dispatch: Dispatch,
  conciliacaoContaDepositoId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<DownloadResponse>(
        `${requestConfig.api}api/v1/investidores/conciliacao/${conciliacaoContaDepositoId}/download-pdf`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const DELETEUploadDoc = async (
  dispatch: Dispatch,
  investidorId: string,
  documentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.delete(`${requestConfig.api}api/v1/investidores/${investidorId}/document/${documentId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETConciliacoesPaginated = async (
  dispatch: Dispatch,
  filter?: GETConciliacaoCDFilterParams,
  stackRequest = false
): Promise<PaginatedResult<ConciliacaoContaDepositoResponse>> => {
  return await request(
    () =>
      axiosInstance.post<PaginatedResult<ConciliacaoContaDepositoResponse>>(
        `${requestConfig.api}api/v1/investidores/conciliacoes/listar`,
        filter
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTConfirmarConciliacaoContaDeposito = async (
  dispatch: Dispatch,
  conciliacaoContaDepositoId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.post(`${requestConfig.api}api/v1/investidores/conciliacao/${conciliacaoContaDepositoId}/confirmar`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTConfirmarConciliacaoContaDepositoEmLote = async (
  dispatch: Dispatch,
  ids: string[],
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.post(`${requestConfig.api}api/v1/investidores/conciliacao/confirmar`, ids),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETConciliacoesContasDeposito = async (
  dispatch: Dispatch,
  date: Moment,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<Array<GETConciliacoesContasDepositoResponse>>(
        `${requestConfig.api}api/v1/investidores/conciliacoes`,
        {
          params: { date: date.toISOString() },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETPosicoes = async (
  dispatch: Dispatch,
  posicoesRequest?: GETInvestidorPosicoesRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<Array<PosicaoResponse>>(
        `${requestConfig.api}api/v1/investidores/posicoes`,
        posicoesRequest ? { params: posicoesRequest } : undefined
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETBloqueios = async (dispatch: Dispatch, stackRequest: boolean = false) => {
  const response = await request(
    () => axiosInstance.get<PaginatedResult<BloqueioResponse>>(`${requestConfig.api}api/v1/investidores/bloqueios`),
    dispatch,
    undefined,
    stackRequest
  );
  return response.result;
};

export const POSTBloqueiosPaginated = async (
  dispatch: Dispatch,
  filter?: GETBloqueiosFilterParams,
  stackRequest: boolean = false
): Promise<PaginatedResult<BloqueioResponse>> => {
  const payload = buildBloqueiosPaginatedPayload(filter);

  const data = await request(
    () =>
      axiosInstance.post<PaginatedResult<BloqueioResponse>>(
        `${requestConfig.api}api/v1/investidores/bloqueios/paginated`,
        payload
      ),
    dispatch,
    undefined,
    stackRequest
  );

  return {
    ...data,
    result: (data.result ?? []).map((item) => mapBloqueioResponse(item as any)),
  };
};

export const POSTBloqueiosEmLote = async (
  dispatch: Dispatch,
  requestData: AdicionarBloqueioEmLoteRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.post<Array<ResponseBloqueioEmLote>>(
        `${requestConfig.api}api/v1/investidores/bloqueios/lote`,
        requestData
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTCertidaoTitularidade = async (dispatch: Dispatch, investidorCpfCnpj: string, req: CertidaoTitularidadeRequest, stackRequest: boolean = false) => {
  try {

    const response = await request(
      () => axiosInstance.post<DownloadResponse>(`${requestConfig.api}api/v1/certidoes/titularidade/${investidorCpfCnpj}/download`, req),
      dispatch,
      undefined,
      stackRequest
    );

    await downloadPDF(response.url, TipoCertidao.CertidaoTitularidade);
  } catch (error) {
    console.error("Erro ao gerar ou baixar PDF: ", error);
  }
};

export const POSTCertidaoGravame = async (dispatch: Dispatch, params: Array<string>, stackRequest: boolean = false) => {
  try {
    const downloadPromises = params.map(async (gravame, index) => {
      const response = await request(
        () => axiosInstance.post<DownloadResponse>(`${requestConfig.api}api/v1/certidoes/gravame/${gravame}/download`),
        dispatch,
        undefined,
        stackRequest
      );

      downloadPDF(response.url, TipoCertidao.Gravame);
    });

    await Promise.all(downloadPromises);

  } catch (error) {
    console.error("Erro ao gerar e baixar PDFs:", error);
  }
};

export const POSTCertidaoInadimplencia = async (
  dispatch: Dispatch,
  params: CertidaoInadimplenciaParams,
  stackRequest: boolean = false
) => {
  try {
    const response = await request(
      () => axiosInstance.post<DownloadResponse>(`${requestConfig.api}api/v1/certidoes/inadimplencia/download`,
        params
      ),
      dispatch,
      undefined,
      stackRequest
    );

    await downloadPDF(response.url, TipoCertidao.CertidaoInadimplencia);

  } catch (error) {
    console.error("Erro ao gerar e baixar PDFs:", error);
  }
};


const downloadPDF = async (url: string, tipoCertidao: TipoCertidao) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Erro ao buscar o arquivo PDF');
    }

    const blob = await response.blob();

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    link.download =
      tipoCertidao === TipoCertidao.Gravame ? 'Certidão Bloqueio.pdf' :
        tipoCertidao === TipoCertidao.CertidaoTitularidade ? 'Certidão Titularidade.pdf' :
          tipoCertidao === TipoCertidao.CertidaoInadimplencia ? 'Certidão Inadimplencia.pdf' :
            'Certidão.pdf';

    document.body.appendChild(link);
    link.click();

    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(link);
  } catch (error) {
    console.error('Erro ao baixar o PDF:', error);
  }
};

export const GETBloqueio = async (dispatch: Dispatch, bloqueioId: string, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.get<BloqueioResponse>(`${requestConfig.api}api/v1/investidores/bloqueios/${bloqueioId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTBloqueio = async (
  dispatch: Dispatch,
  bloqueioRequest: BloqueioRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.post<BloqueioResponse>(`${requestConfig.api}api/v1/investidores/bloqueios`, bloqueioRequest),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTUploadBloqueioDocs = async (
  dispatch: Dispatch,
  bloqueioId: string,
  bloqueioDocumentType: BloqueioDocumentType,
  files: File[],
  stackRequest: boolean = false
) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  return await request(
    () =>
      axiosInstance.put<Array<InvestidorDocumentResponse>>(
        `${requestConfig.api}api/v1/investidores/bloqueios/${bloqueioId}/document`,
        formData,
        {
          params: {
            tipo: bloqueioDocumentType,
          },
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTLiberarBloqueio = async (
  dispatch: Dispatch,
  bloqueioId: string,
  justificativaLiberacao?: string,
  files?: File[],
  stackRequest: boolean = false
) => {
  const formData = new FormData();
  files?.forEach((anexo) => {
    formData.append('anexos', anexo);
  });

  const anexos = files && formData;
  const params = !!justificativaLiberacao ? { justificativaLiberacao } : undefined;
  return await request(
    () =>
      axiosInstance.post<BloqueioResponse>(
        `${requestConfig.api}api/v1/investidores/bloqueios/${bloqueioId}/liberar`,
        anexos,
        {
          params,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETBloqueioDocumentDownloadLink = async (
  dispatch: Dispatch,
  bloqueioId: string,
  documentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<string>(
        `${requestConfig.api}api/v1/investidores/bloqueios/${bloqueioId}/document/${documentId}/download`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTExtratos = async (
  dispatch: Dispatch,
  params: AdicionarExtratoRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.post<ExtratoResponse>(
        `${requestConfig.api}api/v1/investidores/extrato`,
        params
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETInvestidorInstrumentos = async (
  dispatch: Dispatch,
  investidorCpfCnpj: string,
  params: GetInvestidorInstrumentosParams,
  stackRequest?: boolean
) => {
  return await request(
    () =>
      axiosInstance.get<Array<InvestidorInstrumentoFinanceiroResponse>>(
        `${requestConfig.api}api/v1/investidores/${investidorCpfCnpj}/instrumentos`,
        { params }
      ),
    dispatch,
    undefined,
    stackRequest
  );
}

export const GETInvestidoresEscriturador = async (
  dispatch: Dispatch,
  params?: GETInvestidoresParams,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<Array<InvestidorResponse>>(
        `${requestConfig.api}api/v1/investidores/escriturador`,
        params ? { params } : undefined
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTFundoInvestidor = async (
  dispatch: Dispatch,
  fundoInvestidorRequest: FundoInvestidorRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.post<InvestidorFundoResponse>(`${requestConfig.api}api/v1/investidores/fundo`, fundoInvestidorRequest),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETFundoInvestidorByInvestidorId = async (
  dispatch: Dispatch,
  investidorId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<InvestidorFundoResponse>(
        `${requestConfig.api}api/v1/investidores/fundo/${investidorId}`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTFundoInvestidor = async (
  dispatch: Dispatch,
  investidorId: string,
  fundoInvestidorRequest: FundoInvestidorRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.put<InvestidorFundoResponse>(
        `${requestConfig.api}api/v1/investidores/fundo/${investidorId}`,
        fundoInvestidorRequest
      ),
    dispatch,
    undefined,
    stackRequest
  );
};